/* eslint-disable @typescript-eslint/no-explicit-any */
import { ConstructorType, KeyObject } from '../interface/index';
/**
 * 判断值的类型
 * @param value 需要判断的值
 * @param type 需要判断的类型
 */
export function is(
  value: any,
  type: ConstructorType,
) {
  return Object.prototype.toString.call(value) === `[object ${type.name}]`;
}

/**
 * 冻结对象，对象/数组不可修改(对于只用作展示的数据时，可以提高性能)
 * @param source 源数据
 * @param options 配置选择
 *
 * 冻结模式 [self:只冻结souce数据本身, sub:只冻结source的子属性/item, all:冻结所有]
 * 默认all
 */
export function freeze<T>(
  source: T,
  options: {
    mode: 'all' | 'self' | 'sub';
  } = { mode: 'all' },
) {
  const { mode } = options;

  if (is(source, Object)) {
    const obj = source as KeyObject<any>;
    if (['all', 'sub'].includes(mode)) {
      Object.keys(obj).forEach((key) => {
        obj[key] = freeze(obj[key], { mode: 'all' });
      });
    }

    if (['all', 'self'].includes(mode)) {
      Object.freeze(source);
    }
  } else if (is(source, Array)) {
    const arr = (source as unknown) as any[];
    if (['all', 'sub'].includes(mode)) {
      arr.forEach((item, index) => {
        arr[index] = freeze(item, { mode: 'all' });
      });
    }
    if (['all', 'self'].includes(mode)) {
      Object.freeze(source);
    }
  }
  return source;
}

/**
 * 延迟后续代码执行
 * @param interval 延迟的时间 默认0ms
 */
export function $delay(interval = 0): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(), interval);
  });
}

/**
 *  将一个数组串行进行异步处理
 * @param list 原始数据数组
 * @param callback 处理函数
 * @param parallelCount 并行处理的个数, 默认是1
 * @returns {{R[]}} callback返回的异步数据的的数组。
 *
 */
export async function serialAsync<T, R>(
  list: T[],
  callback: (item: T) => Promise<R>,
  parallelCount = 1,
) {
  const parallelList: T[][] = [];
  const len = list.length;
  for (let i = 0; i < len; i += parallelCount) {
    parallelList.push(list.slice(i, i + parallelCount));
  }
  const result = await parallelList.reduce<Promise<R[]>>(
    async (lastResultPromise: Promise<R[]>, subList: T[]) => {
      const lastResult = await lastResultPromise;
      const currentResult = await Promise.all(subList.map((item) => callback(item)));
      return [...lastResult, ...currentResult];
    },
    Promise.resolve([]),
  );
  return result;
}


/**
 * 金额千分位展示
 * @param {*} num
 * @returns ¥111,111,111.00
 */
export function handleNum(num: number | string): any {
  const opts = {
    style: 'currency',
    currency: 'CNY',
  };
  const uNum: any = num;
  if (uNum === '--' || uNum === null || uNum === undefined) {
    return '--';
  } else if (typeof uNum === 'string' && uNum.includes('¥')) {
    return uNum;
  } else {
    const sNum = Number(num);
    return sNum.toLocaleString('zh-CN', opts);
  }
}

/**
 * 构造树型结构数据
 * @param {*} data 数据源
 * @param {*} id id字段 默认 'id'
 * @param {*} parentId 父节点字段 默认 'parentId'
 * @param {*} children 孩子节点字段 默认 'children'
 */
export function handleTree<T>(data: T[], id: string, parentId: string, children: string) {
  const config: any = {
    id: id || 'id',
    parentId: parentId || 'parentId',
    childrenList: children || 'children',
  };

  const childrenListMap: Record<string, T[]> = {};
  const nodeIds: Record<string, T> = {};
  const tree: T[] = [];

  for (const d of data) {
    const d1 = d as any;
    const parentCId = d1[config.parentId];
    if (childrenListMap[parentCId] == null) {
      childrenListMap[parentCId] = [];
    }
    nodeIds[d1[config.id]] = d;
    childrenListMap[parentCId].push(d);
  }

  for (const d of data) {
    const d1 = d as any;
    const parentCId = d1[config.parentId];
    if (nodeIds[parentCId] == null) {
      tree.push(d);
    }
  }

  for (const t of tree) {
    adaptToChildrenList(t);
  }

  function adaptToChildrenList(o: any) {
    if (childrenListMap[o[config.id]] !== null) {
      o[config.childrenList] = childrenListMap[o[config.id]];
    }
    if (o[config.childrenList]) {
      for (const c of o[config.childrenList]) {
        adaptToChildrenList(c);
      }
    }
  }
  return tree;
}


/**
 * 验证密码强度
 * @param password 需要验证的密码
 * @returns 返回是否满足条件
 */
export function validatePassword(password: string): {
  isPass: boolean;
  message: string;
} {
  // 1. 长度至少为8
  if (password.length < 8) {
    return {
      isPass: false,
      message: '密码长度至少需要8个字符。',
    };
  }

  // 2. 包含数字、字母、特殊符号中的任意两种
  const hasLetter = /[a-zA-Z]/.test(password); // 是否包含字母
  const hasDigit = /[0-9]/.test(password); // 是否包含数字
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password); // 是否包含特殊字符

  const typeCount = [hasLetter, hasDigit, hasSpecial].filter(Boolean).length;
  if (typeCount < 2) {
    return {
      isPass: false,
      message: '密码必须包含数字、字母、特殊符号中的任意两种。',
    };
  }

  // 3. 检查是否包含连续的字母或数字（三位以上）
  const hasSequentialChars = (str: string): boolean => {
    for (let i = 0; i < str.length - 2; i++) {
      const current = str.charCodeAt(i);
      const next = str.charCodeAt(i + 1);
      const nextNext = str.charCodeAt(i + 2);

      // 检查连续字母（如abc、def）
      if (
        current >= 65 && current <= 90 && // 大写字母
        next === current + 1 &&
        nextNext === next + 1
      ) {
        return true;
      }
      if (
        current >= 97 && current <= 122 && // 小写字母
        next === current + 1 &&
        nextNext === next + 1
      ) {
        return true;
      }

      // 检查连续数字（如123、456）
      if (
        current >= 48 && current <= 57 && // 数字
        next === current + 1 &&
        nextNext === next + 1
      ) {
        return true;
      }
    }
    return false;
  };

  if (hasSequentialChars(password)) {
    return {
      isPass: false,
      message: '密码不能包含连续的字母或数字。(如：123、abc、123abc)',
    };
  }

  // 4. 检查是否包含连续相同的字符（三位以上，如aaa、111）
  const hasRepeatedChars = (str: string): boolean => {
    for (let i = 0; i < str.length - 2; i++) {
      const current = str[i];
      const next = str[i + 1];
      const nextNext = str[i + 2];

      // 检查连续相同的字符
      if (current === next && next === nextNext) {
        return true;
      }
    }
    return false;
  };

  if (hasRepeatedChars(password)) {
    return {
      isPass: false,
      message: '密码不能包含连续相同的字母或数字（如：111、aaa）',
    };
  }

  // 如果通过所有检查，返回true
  return {
    isPass: true,
    message: '密码符合要求。',
  };
}


/**
 * 获取当前时间
 * @returns 当前月份
 */
export function getDateMonth(val: any) {
  const currentDate = new Date(val);
  const currentMonth = currentDate.getMonth() + 1;
  const _D = `${currentDate.getFullYear()}-${currentMonth < 10 ? `0${currentMonth}` : currentMonth}`;
  return _D;
}

/**
 * 获取当前时间
 * @returns 当前月份
 */
export function getCurrentMonth() {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const _D = `${currentDate.getFullYear()}-${currentMonth < 10 ? `0${currentMonth}` : currentMonth}`;
  return _D;
}

/**
 * 获取当前时间
 * @returns 当前上个月
 */
export function getLastMonth() {
  const now = new Date();
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const year = lastMonthDate.getFullYear();
  const month = String(lastMonthDate.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * 获取当前时间
 * @returns 当前时间
 */
export function getCurrentTime() {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentDay = currentDate.getDate();
  const _D = `${currentDate.getFullYear()}-${currentMonth < 10 ? `0${currentMonth}` : currentMonth}-${currentDay < 10 ? `0${currentDay}` : currentDay}`;
  return _D;
}


/**
 * 获取当前时间
 * @returns 当前时间
 */
export function getCurrentTimeSecond() {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentDay = currentDate.getDate();
  const currentHour = currentDate.getHours();
  const currentMinute = currentDate.getMinutes();
  const currentSecond = currentDate.getSeconds();
  const _D = `${currentDate.getFullYear()}-${currentMonth < 10 ? `0${currentMonth}` : currentMonth}-${currentDay < 10 ? `0${currentDay}` : currentDay} ${currentHour < 10 ? `0${currentHour}` : currentHour}:${currentMinute < 10 ? `0${currentMinute}` : currentMinute}:${currentSecond < 10 ? `0${currentSecond}` : currentSecond}`;
  return _D;
}

/**
 * 验证统一社会信用代码（纳税人识别号）
 * @param taxNumber - 待验证的18位统一社会信用代码
 * @returns 验证结果（true=有效，false=无效）
 */
export function validateTaxNumber(code: string): boolean {
  if (code.length !== 18) {
    return false;
  }

  // 各位加权因子
  const weights = [1, 3, 9, 27, 19, 26, 16, 17, 20, 29, 25, 13, 8, 24, 10, 30, 28];

  // 校验字符集
  const chars = '0123456789ABCDEFGHJKLMNPQRTUWXY';

  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const char = code[i];
    const charIndex = chars.indexOf(char);
    if (charIndex === -1) {
      return false;
    }
    sum += charIndex * weights[i];
  }

  const checksumIndex = (31 - (sum % 31)) % 31;
  const checksumChar = chars[checksumIndex];

  return code[17] === checksumChar;
}


/**
 * 严格校验车辆 VIN 码（含第9位校验位验证）
 * @param vin - 车辆识别号
 * @returns 是否为有效VIN
 */
export function isValidVIN(vin: string): boolean {
  // 转大写并去除空格
  const normalizedVIN = vin.trim().toUpperCase();

  // 1. 长度必须为17
  if (normalizedVIN.length !== 17) {
    return false;
  }

  // 2. 格式校验：只允许 A-H, J-N, P, R-Z, 0-9
  const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/;
  if (!vinRegex.test(normalizedVIN)) {
    return false;
  }

  // 3. 定义权重因子（第9位权重为0，不参与计算）
  const weights: number[] = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

  // 4. 字符映射表（A=1, B=2, ..., Y=25, Z=26），I/O/Q 已被正则排除
  const charValueMap: { [key: string]: number } = {};
  for (let i = 0; i < 26; i++) {
    const char = String.fromCharCode(65 + i); // A-Z
    if (['I', 'O', 'Q'].includes(char)) continue;
    charValueMap[char] = i + 1;
  }
  // 数字 0-9
  for (let i = 0; i <= 9; i++) {
    charValueMap[i.toString()] = i;
  }

  // 5. 加权求和
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const char = normalizedVIN[i];
    const value = charValueMap[char];
    if (value === undefined) return false; // 安全兜底
    sum += value * weights[i];
  }

  // 6. 计算模11结果
  const remainder = sum % 11;
  const checkDigit = remainder === 10 ? 'X' : remainder.toString();

  // 7. 比较第9位（索引8）
  console.log(checkDigit, remainder, normalizedVIN);
  return checkDigit === normalizedVIN[8];
}


/**
 * 使用对象替换字符串中的 {key} 占位符
 * @param template - 包含 {key} 占位符的模板字符串
 * @param data - 包含替换数据的对象
 * @returns 替换后的字符串
 */
export function replacePlaceholders(template: string, data: Record<string, any>): string {
  return template.replace(/\{([^}]+)\}/g, (match, key) => {
    // match 是完整的匹配，如 "{name}"
    // key 是捕获组的内容，即 "name"
    return data[key] !== undefined ? String(data[key]) : match; // 如果 key 不存在，保留原占位符
  });
}

/**
 * 定义音频数据项的接口
 */
interface AudioItem {
  url: string;
  [key: string]: any; // 允许其他动态属性
}

/**
 * 定义返回的数据源结构
 */
interface DataSource {
  total: number;
  list: Array<AudioItem & { duration: number }>;
}

/**
 * 获取包含音频时长的列表数据
 * @param originalList 原始列表数据 (包含 url 字段)
 * @param total 总数 (通常来自后端分页响应)
 * @returns Promise<DataSource> 所有音频时长解析完成后的数据源
 */
export async function getAudioListWithDuration(
  originalList: AudioItem[],
  total: number,
): Promise<DataSource> {
  /**
   * 内部辅助函数：处理单个音频项的时长获取
   */
  const processSingleItem = async (item: AudioItem): Promise<AudioItem & { duration: number }> => {
    // 创建一个新的对象，避免直接修改原数据引用，并初始化 duration 为 0
    const result: AudioItem & { duration: number } = {
      ...item,
      duration: 0,
    };

    // 如果 URL 不存在，直接返回 duration=0
    if (!item.url) {
      console.warn('Missing URL for item:', item);
      return result;
    }

    let audioContext: AudioContext | null = null;

    try {
      // 1. 发起网络请求
      const response = await fetch(item.url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // 2. 获取 ArrayBuffer
      const arrayBuffer = await response.arrayBuffer();

      // 3. 初始化 AudioContext (兼容写法)
      const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;

      if (!AudioContextClass) {
        console.warn('Web Audio API is not supported in this browser.');
        return result;
      }

      audioContext = new AudioContextClass();
      if (audioContext === null) {
        result.call_time = 0;
        return result;
      }
      // 4. 解码音频数据
      // decodeAudioData 是异步的，返回 Promise
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      // 5. 赋值时长
      result.call_time = audioBuffer.duration;
    } catch (error) {
      // 捕获所有错误 (网络、解码、浏览器不支持等)
      console.error(`Failed to get duration for ${item.url}:`, error);
      // 失败时保持 duration 为 0
      result.call_time = 0;
    } finally {
      // 6. 清理资源：关闭 AudioContext
      if (audioContext) {
        try {
          await audioContext.close();
        } catch (e) {
          // 忽略关闭时的错误
        }
      }
    }

    return result;
  };

  // 将列表中的每一项映射为一个 Promise
  const promiseList = originalList.map((item) => processSingleItem(item));

  // 等待所有 Promise 完成
  // 由于 processSingleItem 内部已经处理了 reject (通过 try/catch 返回了默认值)，
  // 这里的 Promise.all 一定会成功 resolve
  const completedList = await Promise.all(promiseList);

  // 组装最终数据
  const dataSource: DataSource = {
    total,
    list: completedList,
  };

  return dataSource;
}
